import { createReducer, on, createFeature } from '@ngrx/store';
import {
  CalendarIntegrationsVendor,
  IntegratedCalendar,
  MicrosoftInfo,
  SalesforceInfo,
  DebtXProfile,
} from '@qtek/shared/models';
import { IntegrationsActions } from './integrations.actions';

export interface IntegrationState {
  availableCalendars: CalendarIntegrationsVendor[] | null;
  appleCalendar: IntegratedCalendar | null;
  googleCalendar: IntegratedCalendar | null;
  microsoft: MicrosoftInfo | null;
  salesforce: SalesforceInfo | null;
  debtX: DebtXProfile | null;
  clover: { expStr: string } | null;
}

const initialState: IntegrationState = {
  availableCalendars: null,
  appleCalendar: null,
  googleCalendar: null,
  microsoft: null,
  salesforce: null,
  debtX: null,
  clover: null,
};

// Reducer
export const integrationsReducer = createReducer(
  initialState,
  on(IntegrationsActions.getCalendarsSuccess, (state, { payload }) => ({
    ...state,
    availableCalendars: payload || [],
  })),
  on(IntegrationsActions.getSalesforceSuccess, (state, { payload }) => ({
    ...state,
    salesforce: payload,
  })),
  on(IntegrationsActions.getMicrosoftSuccess, (state, { payload }) => ({
    ...state,
    microsoft: payload,
  })),
  on(IntegrationsActions.getDebtXProfileSuccess, (state, { status, data }) => ({
    ...state,
    availableCalendars: (state.availableCalendars || []).filter(
      calendar => calendar !== CalendarIntegrationsVendor.DEBTX
    ),
    debtX: status === 'DEBTX_CONNECTED' ? data : ({} as DebtXProfile),
  })),
  on(
    IntegrationsActions.updateCalendarSuccess,
    IntegrationsActions.connectCalendarSuccess,
    IntegrationsActions.getCalendarSuccess,
    (state, { payload }) => {
      if (!payload) return state;

      const calendars = (state.availableCalendars || []).slice();

      if (!calendars.some(cal => cal === payload.vnd)) {
        calendars.push(payload.vnd);
      }

      switch (payload.vnd) {
        case CalendarIntegrationsVendor.APPLE:
          return {
            ...state,
            appleCalendar: payload,
            availableCalendars: calendars,
          };
        case CalendarIntegrationsVendor.GOOGLE:
          return {
            ...state,
            googleCalendar: payload,
            availableCalendars: calendars,
          };
        default:
          return state;
      }
    }
  ),
  on(
    IntegrationsActions.calendarAuthError,
    IntegrationsActions.disconnectCalendarSuccess,
    (state, { payload }) => {
      if (!payload) return state;

      const vendor = payload;
      const calendars = (state.availableCalendars || []).filter(
        calendar => calendar !== vendor
      );

      switch (vendor) {
        case CalendarIntegrationsVendor.APPLE:
          return {
            ...state,
            appleCalendar: null,
            availableCalendars: calendars,
          };
        case CalendarIntegrationsVendor.GOOGLE:
          return {
            ...state,
            googleCalendar: null,
            availableCalendars: calendars,
          };
        case CalendarIntegrationsVendor.MICROSOFT:
          return {
            ...state,
            microsoft: null,
            availableCalendars: calendars,
          };
        case CalendarIntegrationsVendor.SALESFORCE:
          return {
            ...state,
            salesforce: null,
            availableCalendars: calendars,
          };
        case CalendarIntegrationsVendor.DEBTX:
          return {
            ...state,
            debtX: null,
            availableCalendars: calendars,
          };
        default:
          return state;
      }
    }
  ),
  on(IntegrationsActions.connectDebtXSuccess, (state, { status }) => {
    const calendars =
      status === 'DEBTX_CONNECTED'
        ? (state.availableCalendars || []).concat([
            CalendarIntegrationsVendor.DEBTX,
          ])
        : (state.availableCalendars || []).filter(
            calendar => calendar !== CalendarIntegrationsVendor.DEBTX
          );

    return {
      ...state,
      availableCalendars: calendars,
    };
  })
);

export const IntegrationsFeature = createFeature({
  name: 'integrations',
  reducer: integrationsReducer,
});

export const {
  selectAvailableCalendars,
  selectAppleCalendar,
  selectGoogleCalendar,
  selectMicrosoft,
  selectClover,
  selectSalesforce,
  selectDebtX,
} = IntegrationsFeature;
